import { plural } from '@frameio/i18n';
import { Text } from '@frameio/vapor';
import type { AssetResultMatch } from '../../../../../shared/types';
import { FeaturedField } from './FeaturedField';
import styles from './FeaturedFieldMatches.module.css';
import TEXT from './generated/TEXT/FeaturedFieldMatches';
import { useFeaturedMatches } from './useFeaturedMatches';

TEXT.DEFINE({
  /** Label showing match count only (narrow screen widths) */
  countLabel: ({ count }: { count: number }) =>
    `${count} ${plural(count, 'matches', 'match')}`,
  /** Label showing additional match count with pluralization */
  plusCountLabel: ({ count }: { count: number }) =>
    `+${count} more ${plural(count, 'matches', 'match')}`,
});

type FeaturedFieldMatchesProps = {
  featuredMatchCount?: number;
  isActive: boolean;
  matches: AssetResultMatch[];
};

export const FeaturedFieldMatches = ({
  featuredMatchCount,
  matches,
  isActive,
}: FeaturedFieldMatchesProps) => {
  const { featuredMatches, additionalMatches } = useFeaturedMatches(
    matches,
    featuredMatchCount
  );

  const hasFeatureMatches = !!featuredMatches.length;
  const hasAdditionalMatches = !!additionalMatches.length;

  if (!hasFeatureMatches && !hasAdditionalMatches) return null;

  return (
    <div className={styles.flexContainer}>
      {featuredMatches.map((match, index) => (
        <FeaturedField
          key={`${match?.__typename}-${index}`}
          fieldMatch={match}
          isActive={isActive}
        />
      ))}
      {hasAdditionalMatches && (
        <FeaturedField isActive={isActive} style={{ flex: '0 0 auto' }}>
          <Text color="tertiary" variant="caption">
            {hasFeatureMatches
              ? TEXT.plusCountLabel({ count: additionalMatches.length })
              : TEXT.countLabel({ count: additionalMatches.length })}
          </Text>
        </FeaturedField>
      )}
    </div>
  );
};
